import React, { useCallback, useRef, useState, useEffect } from 'react';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';

const libraries: ("places" | "marker")[] = ["places", "marker"];

// Declare the PlaceAutocompleteElement type for TypeScript
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'gmp-place-autocomplete': any;
    }
  }
}

interface LocationMapPickerProps {
  address: string;
  latitude?: number;
  longitude?: number;
  onAddressChange: (address: string) => void;
  onCoordinatesChange: (lat: number, lng: number) => void;
  apiKey: string;
}

const LocationMapPicker: React.FC<LocationMapPickerProps> = ({
  address,
  latitude,
  longitude,
  onAddressChange,
  onCoordinatesChange,
  apiKey,
}) => {
  // Use useLoadScript hook instead of LoadScript component to prevent duplicate loading
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: libraries,
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<{ lat: number; lng: number } | null>(
    latitude && longitude ? { lat: latitude, lng: longitude } : null
  );
  const autocompleteRef = useRef<HTMLElement | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const autocompleteContainerRef = useRef<HTMLDivElement | null>(null);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>(
    latitude && longitude
      ? { lat: latitude, lng: longitude }
      : { lat: 53.3498, lng: -6.2603 } // Default to Dublin
  );

  // Update map center when latitude/longitude props change
  useEffect(() => {
    if (latitude && longitude) {
      setMapCenter({ lat: latitude, lng: longitude });
      setSelectedPosition({ lat: latitude, lng: longitude });
      if (map) {
        map.setCenter({ lat: latitude, lng: longitude });
        map.setZoom(15);
      }
    }
  }, [latitude, longitude, map]);

  // Sync input value when address prop changes externally
  useEffect(() => {
    if (autocompleteContainerRef.current) {
      const inputElement = autocompleteContainerRef.current.querySelector('#place-autocomplete-input') as HTMLInputElement;
      if (inputElement && inputElement.value !== address) {
        inputElement.value = address;
      }
    }
  }, [address]);

  // Initialize PlaceAutocompleteElement and handle place selection
  useEffect(() => {
    if (!isLoaded || !autocompleteContainerRef.current) return;

    const container = autocompleteContainerRef.current;
    let autocompleteElement: any = null;
    let eventHandler: ((event: any) => void) | null = null;
    let retryTimeout: NodeJS.Timeout | null = null;
    let retryCount = 0;
    const maxRetries = 50; // Max 5 seconds of retries
    
    // Function to process placePrediction and update the map
    const processPlacePrediction = async (placePrediction: any) => {
      try {
        console.log('Place prediction received:', placePrediction); // Debug

        if (!placePrediction) {
          console.warn('No placePrediction provided');
          return;
        }

        // Convert prediction to Place object and fetch fields
        try {
          const place = placePrediction.toPlace();
          await place.fetchFields({
            fields: ['displayName', 'formattedAddress', 'location']
          });

          console.log('Place fetched:', place); // Debug

          if (place.location) {
            const location = place.location;
            let lat: number;
            let lng: number;

            // Handle both LatLng object and LatLngLiteral
            if (location instanceof google.maps.LatLng) {
              lat = location.lat();
              lng = location.lng();
            } else if (location && typeof location.lat === 'number' && typeof location.lng === 'number') {
              lat = location.lat;
              lng = location.lng;
            } else {
              console.error('Invalid location format:', location);
              return;
            }

            const formattedAddress = place.formattedAddress || place.displayName || address;

            console.log('Updating map to:', lat, lng, formattedAddress); // Debug

            setSelectedPosition({ lat, lng });
            setMapCenter({ lat, lng });

            // Update the input value to show the selected address
            const inputElement = container.querySelector('#place-autocomplete-input') as HTMLInputElement;
            if (inputElement && formattedAddress) {
              inputElement.value = formattedAddress;
            }
            
            // Update both address and coordinates
            // Call callbacks to update parent component state
            console.log('Calling onAddressChange with:', formattedAddress);
            onAddressChange(formattedAddress);
            
            console.log('Calling onCoordinatesChange with:', lat, lng);
            onCoordinatesChange(lat, lng);

            // Center map on selected location
            if (map) {
              map.setCenter({ lat, lng });
              map.setZoom(15);
            }
          } else {
            console.warn('Place has no location:', place);
          }
        } catch (fetchError) {
          console.error('Error fetching place details:', fetchError);
        }
      } catch (error) {
        console.error('Error handling place select:', error);
      }
    };

    // Fallback geocoding function
    const geocodeAddress = (addressValue: string) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: addressValue }, (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();
          
          console.log('Geocoded address to:', lat, lng);
          
          setSelectedPosition({ lat, lng });
          setMapCenter({ lat, lng });
          onAddressChange(results[0].formatted_address);
          onCoordinatesChange(lat, lng);
          
          if (map) {
            map.setCenter({ lat, lng });
            map.setZoom(15);
          }
        }
      });
    };
    
    // Wait for custom element to be defined and connected
    const initAutocomplete = () => {
      // Find the gmp-place-autocomplete element
      autocompleteElement = container.querySelector('gmp-place-autocomplete');
      if (!autocompleteElement) {
        retryCount++;
        if (retryCount < maxRetries) {
          // Retry after a short delay if element not found
          retryTimeout = setTimeout(initAutocomplete, 100);
        }
        return;
      }

      // Wait for element to be fully connected to DOM
      if (!autocompleteElement.isConnected) {
        retryCount++;
        if (retryCount < maxRetries) {
          retryTimeout = setTimeout(initAutocomplete, 100);
        }
        return;
      }

      autocompleteRef.current = autocompleteElement;

      // Listen to the correct event name: 'gmp-select'
      // According to Google Maps docs, the event handler receives {placePrediction} via destructuring
      eventHandler = async (event: any) => {
        console.log('Event handler called with:', event);
        console.log('Event type:', typeof event);
        console.log('Event keys:', Object.keys(event || {}));
        
        // Try multiple ways to access placePrediction
        // The event might provide placePrediction directly or via destructuring
        let placePrediction = event.placePrediction;
        
        // If not found, try accessing from detail
        if (!placePrediction && event.detail) {
          placePrediction = event.detail.placePrediction;
        }
        
        console.log('Extracted placePrediction:', placePrediction);
        
        if (placePrediction) {
          // Process the place prediction
          await processPlacePrediction(placePrediction);
        } else {
          console.warn('placePrediction not found, trying fallback geocoding');
          // Fallback: try to get the input value and geocode it
          const inputElement = container.querySelector('#place-autocomplete-input') as HTMLInputElement;
          if (inputElement && inputElement.value) {
            geocodeAddress(inputElement.value);
          }
        }
      };
      
      autocompleteElement.addEventListener('gmp-select', eventHandler);
      
      // Also listen to input blur/change as fallback - geocode the address manually
      const inputElement = container.querySelector('#place-autocomplete-input') as HTMLInputElement;
      if (inputElement) {
        const handleInputBlur = () => {
          const inputValue = inputElement.value.trim();
          if (inputValue && inputValue !== address) {
            geocodeAddress(inputValue);
          }
        };
        
        // Use a debounced approach - wait a bit after user stops typing
        let blurTimeout: NodeJS.Timeout | null = null;
        inputElement.addEventListener('blur', () => {
          if (blurTimeout) clearTimeout(blurTimeout);
          blurTimeout = setTimeout(handleInputBlur, 500);
        });
      }
      
      console.log('Event listeners attached to autocomplete element');
    };

    // Check if custom element is already defined, otherwise wait
    if (customElements.get('gmp-place-autocomplete')) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(initAutocomplete, 100);
    } else {
      customElements.whenDefined('gmp-place-autocomplete').then(() => {
        setTimeout(initAutocomplete, 100);
      });
    }

    return () => {
      // Cleanup
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      if (autocompleteElement && eventHandler) {
        autocompleteElement.removeEventListener('gmp-select', eventHandler);
      }
    };
  }, [isLoaded, map, onAddressChange, onCoordinatesChange, address]);

  const onMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      
      setSelectedPosition({ lat, lng });
      setMapCenter({ lat, lng });
      onCoordinatesChange(lat, lng);
      
      // Reverse geocode to get address
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        if (status === 'OK' && results && results[0]) {
          onAddressChange(results[0].formatted_address);
        }
      });
    }
  }, [onAddressChange, onCoordinatesChange]);

  const onMapLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
    if (latitude && longitude) {
      mapInstance.setCenter({ lat: latitude, lng: longitude });
      mapInstance.setZoom(15);
    }
  }, [latitude, longitude]);

  // Create and manage AdvancedMarkerElement
  useEffect(() => {
    if (!isLoaded || !map || !selectedPosition) {
      // Clean up marker if position is cleared
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      return;
    }

    // Create AdvancedMarkerElement if it doesn't exist
    if (!markerRef.current) {
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: map,
        position: { lat: selectedPosition.lat, lng: selectedPosition.lng },
        gmpDraggable: true,
      });

      // Handle drag end event
      marker.addListener('dragend', () => {
        const position = marker.position;
        if (position) {
          let lat: number;
          let lng: number;
          
          // Handle both LatLng object and LatLngLiteral
          if (position instanceof google.maps.LatLng) {
            lat = position.lat();
            lng = position.lng();
          } else {
            lat = position.lat;
            lng = position.lng;
          }
          
          setSelectedPosition({ lat, lng });
          setMapCenter({ lat, lng });
          onCoordinatesChange(lat, lng);
          
          // Reverse geocode
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
            if (status === 'OK' && results && results[0]) {
              onAddressChange(results[0].formatted_address);
            }
          });
        }
      });

      markerRef.current = marker;
    } else {
      // Update marker position if it already exists
      const newPosition = { lat: selectedPosition.lat, lng: selectedPosition.lng };
      markerRef.current.position = newPosition;
    }

    return () => {
      // Only clean up on unmount, not on every position change
      // The marker will be reused and position updated
    };
  }, [isLoaded, map, selectedPosition, onAddressChange, onCoordinatesChange]);

  // Cleanup marker on unmount
  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
    };
  }, []);

  // Show loading state
  if (loadError) {
    return <div className="text-red-500">Error loading Google Maps: {loadError.message}</div>;
  }

  if (!isLoaded) {
    return <div className="text-neutral-500">Loading map...</div>;
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Search Location
        </label>
        <div className="relative" ref={autocompleteContainerRef}>
          {React.createElement(
            'gmp-place-autocomplete',
            {
              'requested-result-types': 'address',
              style: {
                width: '100%',
                display: 'block',
              },
            },
            React.createElement('input', {
              type: 'text',
              defaultValue: address,
              placeholder: 'Search for an address...',
              className: 'w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              slot: 'input',
              id: 'place-autocomplete-input',
            })
          )}
        </div>
      </div>
      
      <div className="w-full h-64 rounded-md overflow-hidden border border-neutral-300">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={mapCenter}
          zoom={latitude && longitude ? 15 : 10}
          onLoad={onMapLoad}
          onClick={onMapClick}
          options={{
            disableDefaultUI: false,
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: true,
            mapId: 'DEMO_MAP_ID', // Required for AdvancedMarkerElement
          }}
        />
      </div>
      
      {(latitude && longitude) && (
        <div className="text-xs text-neutral-500">
          Coordinates: {latitude.toFixed(6)}, {longitude.toFixed(6)}
        </div>
      )}
    </div>
  );
};

export default LocationMapPicker;
